import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import "./toolbar-popover.css";
import { Tooltip } from "./Tooltip";

/** Toolbar overlays stay in the native top layer and retain their trigger's focus/DOM scope. */
export function ToolbarPopover({ label, triggerContent, triggerClass = "icon-button", children, role = "dialog", menu = false, closeKey, hint, closeOnSelect = false, lazy = false }: {
  label: string; triggerContent: ReactNode; triggerClass?: string; children: ReactNode;
  role?: "menu" | "dialog"; menu?: boolean; closeKey?: string;
  hint?: ReactNode; closeOnSelect?: boolean;
  /** Build optional tools only while their disclosure is open. */
  lazy?: boolean;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const focusLast = useRef(false);
  const [open, setOpen] = useState(false);
  const controls = () => Array.from(panel.current?.querySelectorAll<HTMLElement>(menu ? 'a[href],button:not(:disabled)' : 'a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled)') ?? []);

  function close(restore = false) {
    if (panel.current?.matches(":popover-open")) panel.current.hidePopover();
    setOpen(false);
    if (restore) trigger.current?.focus();
  }
  function show(last = false) {
    focusLast.current = last;
    panel.current?.showPopover();
    setOpen(true);
  }
  useEffect(() => { close(); }, [closeKey]);
  useLayoutEffect(() => {
    if (!open) return;
    const element = panel.current;
    function position() {
      if (!element || !trigger.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const left = (viewport?.offsetLeft ?? 0) + 12;
      const top = (viewport?.offsetTop ?? 0) + 12;
      const width = (viewport?.width ?? innerWidth) - 24;
      const bottom = top + (viewport?.height ?? innerHeight) - 24;
      element.style.width = menu ? "max-content" : `${Math.min(360, width)}px`;
      element.style.minWidth = menu ? `${Math.min(128, width)}px` : "0";
      element.style.maxWidth = `${Math.min(menu ? 260 : 360, width)}px`;
      const panelWidth = element.offsetWidth;
      const below = Math.max(0, bottom - anchor.bottom - 6);
      const above = Math.max(0, anchor.top - top - 6);
      const up = below < 240 && above > below;
      element.style.maxHeight = `${Math.min(480, Math.max(100, up ? above : below))}px`;
      element.style.left = `${Math.max(left, Math.min(anchor.right - panelWidth, left + width - panelWidth))}px`;
      element.style.top = `${up ? Math.max(top, anchor.top - element.getBoundingClientRect().height - 6) : Math.min(anchor.bottom + 6, bottom - 100)}px`;
    }
    position();
    const observer = new ResizeObserver(position);
    if (element) observer.observe(element);
    const items = controls();
    (focusLast.current ? items.at(-1) : items[0])?.focus();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    window.visualViewport?.addEventListener("resize", position);
    const focus = (event: FocusEvent) => {
      if (event.target instanceof Node && !element?.contains(event.target) && event.target !== trigger.current) close();
    };
    document.addEventListener("focusin", focus);
    return () => {
      observer.disconnect(); window.removeEventListener("resize", position); window.removeEventListener("scroll", position, true);
      window.visualViewport?.removeEventListener("resize", position); document.removeEventListener("focusin", focus);
    };
  }, [open, menu]);

  function keydown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(true); }
    if (menu && ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const items = controls();
      const index = items.indexOf(document.activeElement as HTMLElement);
      const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    }
  }
  const button = <button ref={trigger} type="button" className={triggerClass} aria-label={label} aria-haspopup={role}
      aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => panel.current?.matches(":popover-open") ? close(true) : show()}
      onKeyDown={(event) => { if (["ArrowDown", "ArrowUp"].includes(event.key)) { event.preventDefault(); show(menu && event.key === "ArrowUp"); } }}>
      {triggerContent}
    </button>;
  return <div className="toolbar-popover-root">
    {hint ? <Tooltip content={hint} disabled={open}>{button}</Tooltip> : button}
    <div ref={panel} id={id} popover="auto" role={role} aria-label={label} tabIndex={-1}
      className={`toolbar-popover ${menu ? "is-menu" : "is-notifications"}`} onKeyDown={keydown}
      onToggle={(event) => { if (event.target === event.currentTarget) setOpen(event.newState === "open"); }}
      onClick={(event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest("a[href]")) close();
        else if (closeOnSelect && event.target.closest('button:not(:disabled)')) close(true);
      }}>
      {(!lazy || open) && children}
    </div>
  </div>;
}
